import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from "react-simple-maps";
import { Tooltip } from "react-tooltip";
import { motion } from "framer-motion";
import { useLanguage } from "../Context/LanguageContext";
import { useMemberData } from "../hooks/useMemberData";
import useCountUp from "../hooks/useCountUp";
import PageTransition from "./PageTransition";

const geoUrl = process.env.PUBLIC_URL + "/data/countries-110m.json";

// ✅ Google Sheets integration is now enabled
const USE_GOOGLE_SHEETS = true;

// English to Turkish country name mapping (comprehensive)
const countryNamesTurkish = {
  "United Kingdom": "Birleşik Krallık",
  Turkey: "Türkiye",
  Netherlands: "Hollanda",
  Ireland: "İrlanda",
  "United States of America": "Amerika",
  France: "Fransa",
  Germany: "Almanya",
  Sweden: "İsveç",
  Australia: "Avustralya",
  Chile: "Şili",
  Luxembourg: "Lüksemburg",
  Italy: "İtalya",
  Belgium: "Belçika",
  Cyprus: "Kıbrıs",
  // Additional countries
  Spain: "İspanya",
  Portugal: "Portekiz",
  Greece: "Yunanistan",
  Poland: "Polonya",
  Czechia: "Çekya",
  Austria: "Avusturya",
  Switzerland: "İsviçre",
  Denmark: "Danimarka",
  Norway: "Norveç",
  Finland: "Finlandiya",
  Russia: "Rusya",
  Ukraine: "Ukrayna",
  Japan: "Japonya",
  China: "Çin",
  India: "Hindistan",
  Brazil: "Brezilya",
  Argentina: "Arjantin",
  Mexico: "Meksika",
  Canada: "Kanada",
  "New Zealand": "Yeni Zelanda",
  "South Africa": "Güney Afrika",
  Egypt: "Mısır",
  Israel: "İsrail",
  "United Arab Emirates": "Birleşik Arap Emirlikleri",
  "Saudi Arabia": "Suudi Arabistan",
  Qatar: "Katar",
  Singapore: "Singapur",
  Malaysia: "Malezya",
  Indonesia: "Endonezya",
  "South Korea": "Güney Kore",
  Taiwan: "Tayvan",
  Thailand: "Tayland",
  Vietnam: "Vietnam",
  Philippines: "Filipinler",
  Pakistan: "Pakistan",
  Bangladesh: "Bangladeş",
  Iran: "İran",
  Iraq: "Irak",
  Hungary: "Macaristan",
  Romania: "Romanya",
  Bulgaria: "Bulgaristan",
  Croatia: "Hırvatistan",
  Serbia: "Sırbistan",
  Slovenia: "Slovenya",
  Slovakia: "Slovakya",
  Estonia: "Estonya",
  Latvia: "Letonya",
  Lithuania: "Litvanya",
};

// Country ISO codes for flag images (comprehensive)
const countryIsoCodes = {
  "United Kingdom": "gb",
  Turkey: "tr",
  Netherlands: "nl",
  Ireland: "ie",
  "United States of America": "us",
  France: "fr",
  Germany: "de",
  Sweden: "se",
  Australia: "au",
  Chile: "cl",
  Luxembourg: "lu",
  Italy: "it",
  Belgium: "be",
  Cyprus: "cy",
  // Additional countries
  Spain: "es",
  Portugal: "pt",
  Greece: "gr",
  Poland: "pl",
  Czechia: "cz",
  Austria: "at",
  Switzerland: "ch",
  Denmark: "dk",
  Norway: "no",
  Finland: "fi",
  Russia: "ru",
  Ukraine: "ua",
  Japan: "jp",
  China: "cn",
  India: "in",
  Brazil: "br",
  Argentina: "ar",
  Mexico: "mx",
  Canada: "ca",
  "New Zealand": "nz",
  "South Africa": "za",
  Egypt: "eg",
  Israel: "il",
  "United Arab Emirates": "ae",
  "Saudi Arabia": "sa",
  Qatar: "qa",
  Singapore: "sg",
  Malaysia: "my",
  Indonesia: "id",
  "South Korea": "kr",
  Taiwan: "tw",
  Thailand: "th",
  Vietnam: "vn",
  Philippines: "ph",
  Pakistan: "pk",
  Bangladesh: "bd",
  Iran: "ir",
  Iraq: "iq",
  Hungary: "hu",
  Romania: "ro",
  Bulgaria: "bg",
  Croatia: "hr",
  Serbia: "rs",
  Slovenia: "si",
  Slovakia: "sk",
  Estonia: "ee",
  Latvia: "lv",
  Lithuania: "lt",
};

// Helper to get flag image URL from country name
const getFlagUrl = (countryName) => {
  const code = countryIsoCodes[countryName];
  if (code) {
    return `https://flagcdn.com/w40/${code}.png`;
  }
  return null;
};



const MembersMap = () => {
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const { t, language } = useLanguage();

  // Responsive: track window width for dynamic map config
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Get responsive map configuration based on screen width
  const mapConfig = useMemo(() => {
    if (windowWidth <= 480) {
      return { height: 300, scale: 120, center: [15, 35] };
    }
    if (windowWidth <= 768) {
      return { height: 400, scale: 150, center: [15, 30] };
    }
    if (windowWidth <= 1024) {
      return { height: 500, scale: 160, center: [15, 30] };
    }
    return { height: 600, scale: 180, center: [15, 30] };
  }, [windowWidth]);

  // Disable tooltip on touch devices to avoid sticky tooltips
  const isTouchDevice = useMemo(() => {
    return typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0);
  }, []);

  const handleMouseEnter = useCallback((name) => {
    if (!isTouchDevice) setHoveredCountry(name);
  }, [isTouchDevice]);

  const handleMouseLeave = useCallback(() => {
    if (!isTouchDevice) setHoveredCountry(null);
  }, [isTouchDevice]);

  // Use the custom hook - switch to Google Sheets by setting USE_GOOGLE_SHEETS to true
  const { memberCounts, totalMembers, totalCountries, loading } =
    useMemberData(USE_GOOGLE_SHEETS);

  const [membersRef, membersDisplay] = useCountUp(loading ? 0 : totalMembers, 1600, '');
  const [countriesRef, countriesDisplay] = useCountUp(loading ? 0 : totalCountries, 1200, '');

  // Helper to get localized country name
  const getCountryName = (englishName) => {
    if (language === "tr" && countryNamesTurkish[englishName]) {
      return countryNamesTurkish[englishName];
    }
    return englishName;
  };

  // Sort countries by member count
  const sortedCountries = useMemo(() => {
    return Object.entries(memberCounts).sort(([, a], [, b]) => b - a);
  }, [memberCounts]);

  // Get max count for color scaling
  const maxCount = useMemo(() => {
    return Math.max(...Object.values(memberCounts), 1);
  }, [memberCounts]);

  // Get color based on member count (gradient from light to dark blue)
  // When a country is selected, all member countries become uniform light blue
  const getCountryColor = (count, isAnySelected) => {
    if (!count) return "var(--bg-tertiary)";
    if (isAnySelected) return "#93c5fd"; // Uniform light blue when a selection is active
    const intensity = Math.min(count / maxCount, 1);
    // Return different shades based on intensity
    if (intensity > 0.7) return "#1e40af"; // Dark blue
    if (intensity > 0.4) return "#3b82f6"; // Medium blue
    if (intensity > 0.2) return "#60a5fa"; // Light blue
    return "#93c5fd"; // Very light blue
  };

  // Show loading state while fetching from Google Sheets
  if (loading) {
    return (
      <PageTransition>
      <section id="members-map">
        <div
          className="container"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh",
          }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ textAlign: "center" }}
          >
            <div
              className="loading-spinner"
              style={{
                width: "50px",
                height: "50px",
                border: "4px solid var(--bg-tertiary)",
                borderTop: "4px solid var(--primary-color)",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px",
              }}
            />
            <p style={{ color: "var(--text-tertiary)" }}>
              {language === "tr" ? "Veriler yükleniyor..." : "Loading data..."}
            </p>
          </motion.div>
        </div>
      </section>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
    <section id="members-map">
      {/* Hero Header */}
      <div className="map-hero">
        <div className="container">
          <motion.div
            className="map-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>{t("map.title")}</h1>
            <p>{t("map.subtitle")}</p>

            <div className="hero-stats">
              <motion.div
                className="hero-stat"
                ref={membersRef}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
              >
                <span className="hero-stat-number">{membersDisplay}</span>
                <span className="hero-stat-label">
                  {t("map.stats.members")}
                </span>
              </motion.div>
              <div className="hero-stat-divider"></div>
              <motion.div
                className="hero-stat"
                ref={countriesRef}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
              >
                <span className="hero-stat-number">{countriesDisplay}</span>
                <span className="hero-stat-label">
                  {t("map.stats.countries")}
                </span>
              </motion.div>
              <div className="hero-stat-divider"></div>
              <motion.div
                className="hero-stat"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
              >
                <span className="hero-stat-number">3</span>
                <span className="hero-stat-label">
                  {language === "tr" ? "Kıta" : "Continents"}
                </span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="container">
        {/* Map and Country List */}
        <motion.div
          className="map-container-wrapper"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          {/* Map Box */}
          <div className="map-box">
            <ComposableMap
              projectionConfig={{ scale: mapConfig.scale, center: mapConfig.center }}
              height={mapConfig.height}
              style={{ width: "100%", height: "100%" }}
            >
              <ZoomableGroup center={mapConfig.center} zoom={1} minZoom={1} maxZoom={6}>
                <Geographies geography={geoUrl}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const { name } = geo.properties;
                      const count = memberCounts[name];
                      const isHovered = hoveredCountry === name;
                      const isSelected = selectedCountry === name;
                      const isAnySelected = !!selectedCountry;

                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          data-tooltip-id="map-tooltip"
                          data-tooltip-content={
                            count
                              ? `${getCountryName(name)}: ${count}`
                              : getCountryName(name)
                          }
                          onMouseEnter={() => handleMouseEnter(name)}
                          onMouseLeave={handleMouseLeave}
                          onClick={() =>
                            count &&
                            setSelectedCountry(isSelected ? null : name)
                          }
                          style={{
                            default: {
                              fill: isSelected
                                ? "#1e3a8a"
                                : isHovered && count
                                ? "#2563eb"
                                : getCountryColor(count, isAnySelected),
                              outline: "none",
                              stroke: count ? "#fff" : "var(--border-color)",
                              strokeWidth: count ? 0.8 : 0.3,
                              transition: "all 0.2s ease",
                              cursor: count ? "pointer" : "default",
                            },
                            hover: {
                              fill: count ? "#2563eb" : "var(--bg-tertiary)",
                              outline: "none",
                              stroke: count ? "#fff" : "var(--border-color)",
                              strokeWidth: count ? 1 : 0.3,
                            },
                            pressed: {
                              fill: "#1e3a8a",
                              outline: "none",
                            },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>

            <Tooltip
              id="map-tooltip"
              style={{
                backgroundColor: "#1e3a8a",
                color: "#fff",
                borderRadius: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "600",
                zIndex: 1000,
              }}
            />
          </div>

          {/* Country List */}
          <div className="country-list-box">
            <h3 className="country-list-header">
              {language === "tr"
                ? "Ülkelere Göre Üyeler"
                : "Members by Country"}
            </h3>
            <div className="country-list">
              {sortedCountries.map(([country, count]) => (
                <div
                  key={country}
                  className={`country-item ${
                    hoveredCountry === country ? "active" : ""
                  } ${selectedCountry === country ? "selected" : ""}`}
                  onMouseEnter={() => setHoveredCountry(country)}
                  onMouseLeave={() => setHoveredCountry(null)}
                  onClick={() =>
                    setSelectedCountry(
                      selectedCountry === country ? null : country
                    )
                  }
                >
                  <div className="country-info">
                    <span className="country-flag">
                      {getFlagUrl(country) ? (
                        <img
                          src={getFlagUrl(country)}
                          alt={country}
                          className="flag-img"
                        />
                      ) : (
                        <i className="fa fa-globe" aria-hidden="true"></i>
                      )}
                    </span>
                    <span className="country-name">
                      {getCountryName(country)}
                    </span>
                  </div>
                  <span className="member-count">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          className="map-cta"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          <div className="cta-content">
            <h3>
              {language === "tr"
                ? "Topluluğumuza Katılın"
                : "Join Our Community"}
            </h3>
            <p>
              {language === "tr"
                ? "Dünyanın dört bir yanından Kıbrıs Türk profesyonelleriyle bağlantı kurun ve ağınızı genişletin."
                : "Connect with Turkish Cypriot professionals from around the world and expand your network."}
            </p>
            <a
              href="https://forms.gle/y6pgL7uZ1LJJv5tD8"
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button"
            >
              <i className="fa fa-user-plus" aria-hidden="true"></i>
              {language === "tr"
                ? "Üyelik Formuna Git"
                : "Go to Membership Form"}
            </a>
          </div>
        </motion.div>
      </div>
    </section>
    </PageTransition>
  );
};

export default MembersMap;
